/**
 * UsersPage
 *
 * Admin page for viewing and managing beta user accounts.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Search,
  Loader2,
  RefreshCw,
  UserCheck,
  UserX,
  Clock,
  UserPlus,
  X,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react';
import {
  useInviteBetaUsers,
  useGetBetaUsers,
  BetaUserSummary,
  EngagementStatus,
} from '../hooks/useAdminApi';

interface DisplayUserEntry {
  uid: string;
  email: string;
  displayName: string;
  status: EngagementStatus;
  invitedAt: string;
  lastActivityAt: string;
  caseFileCount: number;
}

type FilterTab = 'all' | 'active' | 'at_risk' | 'churned' | 'never_activated';

/**
 * Convert Firestore timestamp to formatted date string
 */
function formatTimestamp(timestamp?: { _seconds: number; _nanoseconds: number }): string {
  if (!timestamp || typeof timestamp._seconds !== 'number') {
    return '—';
  }
  const date = new Date(timestamp._seconds * 1000);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Transform API user to display format
 */
function transformUser(user: BetaUserSummary): DisplayUserEntry {
  return {
    uid: user.userId,
    email: user.email,
    displayName: user.displayName || user.email.split('@')[0],
    status: user.engagementStatus,
    invitedAt: formatTimestamp(user.invitedAt),
    lastActivityAt: formatTimestamp(user.lastActivityAt),
    caseFileCount: user.caseFileCount,
  };
}

export function UsersPage(): JSX.Element {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // API hooks
  const { execute, loading, error, data } = useGetBetaUsers();
  const { mutate: inviteBetaUsers, loading: inviting, reset: resetInvite } = useInviteBetaUsers();

  // Create User modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserCohort, setNewUserCohort] = useState('');
  const [createResult, setCreateResult] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Fetch all users on mount (filtering done client-side)
  const fetchUsers = useCallback(async () => {
    try {
      await execute({ limit: 100 });
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [execute]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Transform API users to display format
  const users: DisplayUserEntry[] = data?.users?.map(transformUser) ?? [];

  const handleCreateUser = async () => {
    if (!newUserEmail.trim()) return;

    setCreateResult(null);
    resetInvite();
    try {
      const result = await inviteBetaUsers({
        emails: [newUserEmail.trim()],
        cohort: newUserCohort.trim() || undefined,
      });

      if (result.invited.length > 0) {
        setCreateResult({
          type: 'success',
          message: `${result.invited[0]} has been pre-approved for beta access.`,
        });
        setNewUserEmail('');
        setNewUserCohort('');
        // Refresh user list to show the new entry
        fetchUsers();
      } else if (result.alreadyInvited.length > 0) {
        setCreateResult({
          type: 'error',
          message: `${result.alreadyInvited[0]} already has beta access.`,
        });
      } else if (result.invalid.length > 0) {
        setCreateResult({
          type: 'error',
          message: `"${result.invalid[0]}" is not a valid email address.`,
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to pre-approve user. Please try again.';
      setCreateResult({
        type: 'error',
        message: errorMessage,
      });
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewUserEmail('');
    setNewUserCohort('');
    setCreateResult(null);
    resetInvite();
  };

  // Filter users by engagement status tab and search query
  const filteredUsers = users.filter((user) => {
    const matchesTab = activeTab === 'all' || user.status === activeTab;
    const matchesSearch =
      !searchQuery ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.displayName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  // Use counts from API response
  const counts = {
    all: data?.counts?.total ?? 0,
    active: data?.counts?.active ?? 0,
    at_risk: data?.counts?.atRisk ?? 0,
    churned: data?.counts?.churned ?? 0,
    never_activated: data?.counts?.neverActivated ?? 0,
  };

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'at_risk', label: 'At Risk', count: counts.at_risk },
    { key: 'churned', label: 'Churned', count: counts.churned },
    { key: 'never_activated', label: 'Never Activated', count: counts.never_activated },
  ];

  const statusColors: Record<EngagementStatus, string> = {
    active: 'bg-green-100 text-green-700',
    at_risk: 'bg-yellow-100 text-yellow-700',
    churned: 'bg-red-100 text-red-700',
    never_activated: 'bg-gray-100 text-gray-700',
  };

  const statusLabels: Record<EngagementStatus, string> = {
    active: 'Active',
    at_risk: 'At Risk',
    churned: 'Churned',
    never_activated: 'Never Activated',
  };

  return (
    <div className="min-h-screen bg-airy">
      {/* Header */}
      <header className="bg-white border-b border-soft-neutral sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="btn-ghost btn-sm flex items-center gap-1"
              >
                <ArrowLeft size={16} />
                Admin
              </button>
              <div className="h-6 w-px bg-soft-neutral" />
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-evergreen" />
                <h1 className="text-xl font-semibold text-ink">Users</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-sm flex items-center gap-2 bg-evergreen text-white rounded-lg px-3 py-1.5 text-sm hover:bg-evergreen/90 transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                Create User
              </button>
              <button
                onClick={fetchUsers}
                disabled={loading}
                className="btn-ghost btn-sm flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <p className="font-medium">Failed to load users</p>
            </div>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <button
              onClick={fetchUsers}
              className="mt-2 text-sm text-red-700 underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-ink">{counts.all}</p>
              <p className="text-xs text-muted">Total Users</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-green-600">{counts.active}</p>
              <p className="text-xs text-muted">Active</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-yellow-600">{counts.at_risk}</p>
              <p className="text-xs text-muted">At Risk</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <UserX className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-red-600">{counts.churned}</p>
              <p className="text-xs text-muted">Churned</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-600">{counts.never_activated}</p>
              <p className="text-xs text-muted">Never Activated</p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 transition-colors ${
                activeTab === tab.key
                  ? 'bg-evergreen text-white'
                  : 'bg-soft-neutral text-ink hover:bg-slate-200'
              }`}
            >
              {tab.label}
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${
                  activeTab === tab.key ? 'bg-white/20' : 'bg-slate-300'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full md:w-96"
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted" />
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 mx-auto text-muted mb-4" />
            <p className="text-muted">
              {users.length === 0
                ? 'No beta users found'
                : 'No users match your filters'}
            </p>
          </div>
        )}

        {/* Users Table */}
        {filteredUsers.length > 0 && (
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-soft-neutral/50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-muted">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-muted">CaseFiles</th>
                  <th className="text-left px-4 py-3 font-medium text-muted">Invited</th>
                  <th className="text-left px-4 py-3 font-medium text-muted">Last Activity</th>
                  <th className="text-left px-4 py-3 font-medium text-muted">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-soft-neutral">
                {filteredUsers.map((user) => (
                  <tr key={user.uid} className="hover:bg-soft-neutral/30 transition-colors">
                    <td className="px-4 py-3 text-ink">{user.displayName}</td>
                    <td className="px-4 py-3 text-muted">{user.email}</td>
                    <td className="px-4 py-3 text-muted">{user.caseFileCount}</td>
                    <td className="px-4 py-3 text-muted">{user.invitedAt}</td>
                    <td className="px-4 py-3 text-muted">{user.lastActivityAt}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded ${statusColors[user.status]}`}
                      >
                        {statusLabels[user.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeCreateModal}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-ink">Pre-Approve Beta User</h2>
              <button
                onClick={closeCreateModal}
                className="text-muted hover:text-ink transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-muted mb-4">
              Add an email to the approved list. When this person registers, they will automatically have beta access.
            </p>

            {/* Result banner */}
            {createResult && (
              <div
                className={`flex items-start gap-2 p-3 rounded-lg mb-4 text-sm ${
                  createResult.type === 'success'
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {createResult.type === 'success' ? (
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                )}
                <span>{createResult.message}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  placeholder="user@example.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newUserEmail.trim() && !inviting) {
                      handleCreateUser();
                    }
                  }}
                  className="input w-full"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink mb-1">
                  Cohort <span className="text-muted font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. beta-wave-2, partner-org"
                  value={newUserCohort}
                  onChange={(e) => setNewUserCohort(e.target.value)}
                  className="input w-full"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={closeCreateModal}
                className="btn-ghost btn-sm px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={!newUserEmail.trim() || inviting}
                className="flex items-center gap-2 bg-evergreen text-white rounded-lg px-4 py-2 text-sm hover:bg-evergreen/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inviting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                {inviting ? 'Approving...' : 'Approve User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersPage;
